import React, { useEffect, useState } from 'react';
import {
  getAllBuilderCategories,
  getSingleCommonLiveTask,
  getSingleIndependentLiveTask,
  updateLiveTask,
  getSingleGroupLiveTask,
  getAllItemsByProjectAndCategoryID
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import editIcon from '../../shared/assets/svg/pencil.svg';
import TaskBoqBreakdown from '../components/boq-bom/TaskBoqBreakdown';
import AssignTaskPeopleModal from './AssignTaskPeopleModal';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import { X } from 'lucide-react';

const SingleTaskDetails = ({ apiType, taskID, isDrawerOpen, callApi }) => {
  const [assigning, setAssigning] = useState(null);
  const userId = Cookies.get('userId');
  const UserID = Cookies.get('userId');
  const {
    data: Task,
    refetch: refetchCommonLiveTask,
    isFetching
  } = useQuery({
    // The task type is part of the key: the page sets the selected task id from
    // the date picker and assignee picker too, before any type is known, and an
    // empty result cached under the id alone was then shown for that task.
    queryKey: ['getLiveTask', apiType, taskID],
    queryFn: () => {
      if (!taskID) {
        return Promise.resolve([]);
      }
      return apiType === 'common'
        ? getSingleCommonLiveTask(taskID)
        : apiType === 'independent'
          ? getSingleIndependentLiveTask(taskID)
          : apiType === 'group'
            ? getSingleGroupLiveTask(taskID)
            : Promise.resolve([]);
    },
    enabled: !!taskID && !!apiType
  });

  const { data: AllCategory, refetch: refetchCategories } = useQuery({
    queryKey: ['getCategory', UserID],
    queryFn: () => {
      if (!UserID) {
        return Promise.resolve([]); // protect against undefined values
      }
      return getAllBuilderCategories(UserID);
    },
    enabled: !!UserID
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Single Task Details Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchCategories();
  }, []);

  const colors = ['bg-orange-400', 'bg-blue-500', 'bg-green-500', 'bg-purple-500'];
  const [materialCategories, setMaterialCategories] = useState([
    {
      id: Date.now(),
      items: [{ id: Date.now(), item_id: '', quantity: '' }]
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define selectedProjectId early to avoid reference errors
  const selectedProjectId = Task?.data?.project_id;

  // Function to fetch items by project and category
  const getItemsByProjectAndCategory = async (projectId, categoryId) => {
    if (!projectId || !categoryId) return [];
    try {
      return await getAllItemsByProjectAndCategoryID(projectId, categoryId);
    } catch (error) {
      console.error('Error fetching items:', error);
      return [];
    }
  };
  const [editingBudget, setEditingBudget] = useState(false);
  const [newBudget, setNewBudget] = useState('');
  const [editingMaterialBudget, setEditingMaterialBudget] = useState(false);
  const [materialBudget, setMaterialBudget] = useState('');
  const [editingMaterialIndex, setEditingMaterialIndex] = useState(null);
  const [editingMaterialData, setEditingMaterialData] = useState(null);
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const { mutate: updateTask } = useMutation({
    mutationFn: ({ id, payload }) => updateLiveTask(id, payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Task updated successfully');
      setMaterialCategories([
        {
          id: Date.now(),
          items: [{ id: Date.now(), item_id: '', quantity: '' }]
        }
      ]);
      // Reset editing state
      setEditingMaterialIndex(null);
      setEditingMaterialData(null);
      setShowMaterialModal(false);
      await refetchCommonLiveTask();
      await callApi?.();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating task', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating task');
    }
  });

  const formatDate = (date) => {
    if (!date) return '-';
    const options = { day: 'numeric', month: 'short' };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const handleAddCategory = () => {
    setMaterialCategories([
      ...materialCategories,
      {
        id: Date.now() + Math.random(),
        items: [{ id: Date.now() + Math.random(), item_id: '', quantity: '' }]
      }
    ]);
  };

  const handleAddItem = (categoryId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: [
                ...category.items,
                { id: Date.now() + Math.random(), item_id: '', quantity: '' }
              ]
            }
          : category
      )
    );
  };

  const handleRemoveItem = (categoryId, itemId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.filter((item) => item.id !== itemId)
            }
          : category
      )
    );
  };

  const handleRemoveCategory = (categoryId) => {
    setMaterialCategories((prev) => prev.filter((category) => category.id !== categoryId));
  };

  const handleMaterialBudgetSave = () => {
    const id = Task?.data?.id;
    if (id && materialBudget) {
      const payload = {
        material_budget: Number(materialBudget)
      };
      updateTask({ id, payload });
      setEditingMaterialBudget(false);
    }
  };

  const handleLabourBudgetSave = () => {
    const id = Task?.data?.id;
    if (id && newBudget) {
      const payload = {
        labour_budget: Number(newBudget)
      };
      updateTask({ id, payload });
      setEditingBudget(false);
    }
  };

  useEffect(() => {
    if (isDrawerOpen) {
      // Drawer is closed, reset all local states
      setMaterialCategories([
        {
          id: Date.now(),
          items: [{ id: Date.now(), item_id: '', quantity: '' }]
        }
      ]);
      setIsSubmitting(false);
      setEditingBudget(false);
      setEditingMaterialBudget(false);
      setNewBudget('');
      setMaterialBudget('');
      setEditingMaterialIndex(null);
      setEditingMaterialData(null);
      setShowMaterialModal(false);
    }
  }, [isDrawerOpen]);

  const handleCategoryChange = async (categoryId, selectedCategoryId) => {
    // First update the category immediately for UI feedback
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              category_id: selectedCategoryId,
              available_items: [], // Clear items initially
              items: category.items.map((item) => ({
                ...item,
                category_id: selectedCategoryId,
                item_id: '', // Reset item when category changes
                quantity: ''
              }))
            }
          : category
      )
    );

    // Fetch items for the selected category and project
    let categoryItems = [];
    if (selectedProjectId && selectedCategoryId) {
      try {
        categoryItems = await getItemsByProjectAndCategory(selectedProjectId, selectedCategoryId);

        // Map new API response to available_items for dropdown
        const mappedItems = (categoryItems || []).map((apiItem) => ({
          id: apiItem.id, // OUTER id (parent id)
          name: apiItem.item?.name,
          unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
        }));

        // Update with fetched items
        setMaterialCategories((prev) =>
          prev.map((category) =>
            category.id === categoryId
              ? {
                  ...category,
                  available_items: mappedItems
                }
              : category
          )
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to fetch items for selected category'
        );
      }
    } else {
      if (!selectedProjectId) {
        toast.warning('Project ID not available from task data');
      }
    }
  };

  const handleItemChange = (categoryId, itemId, selectedItemInfo) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      item_id: selectedItemInfo.id,
                      item_name: selectedItemInfo.name,
                      unit: selectedItemInfo.unit
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  const handleQuantityChange = (categoryId, itemId, quantity) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      quantity: quantity
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  const handleAddUpdateMaterials = () => {
    const newMaterials = [];

    for (const category of materialCategories) {
      for (const item of category.items) {
        const hasAnyValue = item.item_id || item.quantity;

        // If any value is provided, require all fields
        if (hasAnyValue) {
          if (!item.item_id || !item.quantity) {
            toast.error('If you select an item, quantity must be provided.');
            return;
          }

          // Get the unit from the selected item
          const selectedUnit = item.unit || 'unit';

          newMaterials.push({
            item_id: Number(item.item_id),
            unit: selectedUnit,
            quantity: Number(item.quantity)
          });
        }
      }
    }

    // Get existing materials from the current task
    const existingMaterials =
      Task?.data?.materialTaskMapping?.map((material) => ({
        item_id: material.item_id,
        unit: material.unit,
        quantity: material.quantity
      })) || [];

    // Combine existing materials with new materials
    const allMaterials = [...existingMaterials, ...newMaterials];

    const id = Task?.data?.id;
    if (id && (newMaterials.length > 0 || existingMaterials.length > 0)) {
      const payload = {
        materials: allMaterials
      };
      console.log('Sending materials payload:', payload);
      updateTask({ id, payload });
    }
  };

  const handleEditMaterial = async (materialIndex, material) => {
    setEditingMaterialIndex(materialIndex);
    setEditingMaterialData(material);

    console.log('material', material);

    // Reset the form and set up for editing
    const categoryId = material.item?.category?.id;
    if (categoryId && selectedProjectId) {
      try {
        // Fetch items for this category
        let categoryItems = await getItemsByProjectAndCategory(selectedProjectId, categoryId);
        let mappedItems = (categoryItems || []).map((apiItem) => ({
          id: apiItem.id, // OUTER id (parent id)
          name: apiItem.item?.name,
          unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
        }));

        // Ensure the current item is present in available_items
        if (!mappedItems.some((item) => String(item.id) === String(material.item_id))) {
          mappedItems = [
            ...mappedItems,
            {
              id: String(material.item_id),
              name: material.item?.name,
              unit: material.unit
            }
          ];
        }

        setMaterialCategories([
          {
            id: Date.now(),
            category_id: categoryId,
            available_items: mappedItems,
            items: [
              {
                id: Date.now(),
                item_id: String(material.item_id),
                item_name: material.item?.name,
                unit: material.unit,
                quantity: material.quantity,
                category_id: categoryId
              }
            ]
          }
        ]);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Failed to load material for editing'
        );
      }
    }
  };

  const handleUpdateMaterial = () => {
    const updatedMaterial = materialCategories[0]?.items[0];

    if (!updatedMaterial?.item_id || !updatedMaterial?.quantity) {
      toast.error('Please fill all required fields.');
      return;
    }

    // Get existing materials
    const existingMaterials =
      Task?.data?.materialTaskMapping?.map((material, index) => {
        if (index === editingMaterialIndex) {
          // Replace with updated material
          return {
            item_id: Number(updatedMaterial.item_id),
            unit: updatedMaterial.unit || 'unit',
            quantity: Number(updatedMaterial.quantity)
          };
        }
        // Keep existing material as is
        return {
          item_id: material.item_id,
          unit: material.unit,
          quantity: material.quantity
        };
      }) || [];

    const id = Task?.data?.id;
    if (id) {
      const payload = {
        materials: existingMaterials
      };
      console.log('Updating material payload:', payload);
      updateTask({ id, payload });
      handleCancelEdit();
    }
  };

  const handleCancelEdit = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialData(null);
    // Reset form
    setMaterialCategories([
      {
        id: Date.now(),
        items: [{ id: Date.now(), item_id: '', quantity: '' }]
      }
    ]);
  };

  const openAddMaterials = () => {
    handleCancelEdit();
    setShowMaterialModal(true);
  };

  const closeMaterialModal = () => {
    handleCancelEdit();
    setShowMaterialModal(false);
  };

  const handlePrepareTableData = () => {
    const tableData = Task?.data?.materialTaskMapping.map((material) => ({
      category: material.item?.category?.name ? material.item.category.name.slice(0, 20) : 'N/A',

      item: material.item?.item_name || 'N/A',
      unit: material.unit || 'N/A',
      quantity: material.quantity ?? 'N/A' // Use ?? to allow 0 quantity
    }));

    return tableData;
  };

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-64 text-gray-600">Loading updates...</div>
    );
  }

  if (!Task) return <div>No Task Selected</div>;

  const taskWorkType = Task?.data?.workType?.name || Task?.data?.template?.workType?.name;
  const taskPlace = [
    Task?.data?.unit?.unitName,
    Task?.data?.levelName || Task?.data?.floorName,
    Task?.data?.section_name
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="p-6 bg-white rounded-md shadow-md">
      {Task?.data?.name ? (
        <div className="mb-5 border-b border-gray-100 pb-4">
          <h2 className="text-lg font-semibold text-gray-900 leading-snug">{Task.data.name}</h2>
          {(taskWorkType || taskPlace) && (
            <p className="mt-1 text-sm text-gray-500">
              {[taskWorkType, taskPlace].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-4 text-sm mb-6">
        <div>
          <p className="font-semibold">
            Planned Date - {formatDate(Task?.data?.planned_start_date)}
          </p>
        </div>
        <div>
          <p className="font-semibold">End Date - {formatDate(Task?.data?.planned_end_date)}</p>
        </div>
      </div>

      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">People on this task</p>
        {Task?.data?.id ? (
          <button
            type="button"
            onClick={() => setAssigning('team')}
            className="rounded-md bg-[#2A565E] px-3 py-1 text-xs font-medium text-white">
            Assign people
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm mb-6">
        <div>
          <p className="font-semibold mb-2">
            Assigned Employee Names{' '}
            <button type="button" onClick={() => setAssigning('team')} className="text-xs font-normal text-[#2A565E] underline">
              {Task?.data?.engineerLiveTaskMapping?.length ? 'Edit' : 'Add'}
            </button>
          </p>
          <div className="flex flex-col gap-2">
            {Task?.data?.engineerLiveTaskMapping?.map((eng, index) => (
              <div key={eng.id} className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                  {eng?.user?.name?.charAt(0).toUpperCase()}
                </div>
                {eng?.user?.name}
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="font-semibold mb-2">
            Assigned Contractor Names{' '}
            <button type="button" onClick={() => setAssigning('contractors')} className="text-xs font-normal text-[#2A565E] underline">
              {Task?.data?.spLiveTaskMapping?.some((sp) => sp?.user?.serviceProvider?.category_id === 1) ? 'Edit' : 'Add'}
            </button>
          </p>
          <div className="flex flex-col gap-2">
            {Task?.data?.spLiveTaskMapping?.map(
              (sp, index) =>
                sp?.user?.serviceProvider?.category_id === 1 && (
                  <div key={sp.id} className="flex items-center gap-2">
                    <div
                      className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                      {sp?.user?.name?.charAt(0).toUpperCase()}
                    </div>
                    {sp?.user?.name}
                  </div>
                )
            )}
          </div>
        </div>
      </div>

      <div className="text-sm mb-6">
        <p className="font-semibold mb-2">
          Assigned Architect Names{' '}
          <button type="button" onClick={() => setAssigning('architects')} className="text-xs font-normal text-[#2A565E] underline">
            {Task?.data?.architectLiveTaskMapping?.some((sp) => sp?.user?.serviceProvider?.category_id === 2) ? 'Edit' : 'Add'}
          </button>
        </p>
        <div className="flex flex-row gap-2 flex-wrap justify-between">
          {Task?.data?.architectLiveTaskMapping?.map(
            (sp, index) =>
              sp?.user?.serviceProvider?.category_id === 2 && (
                <div key={sp.id} className="flex items-center gap-2 w-fit">
                  <div
                    className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                    {sp?.user?.name?.charAt(0).toUpperCase()}
                  </div>
                  {sp?.user?.name}
                </div>
              )
          )}
        </div>
      </div>
      {Task?.data?.subActivity.length > 0 && (
        <div className="text-sm mb-6">
          <p className="font-semibold mb-2">Sub activities list</p>
          <ul className="list-disc list-inside">
            {Task?.data?.subActivity.map((t, index) => (
              <li key={index}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {Task?.data?.boq_id ? <TaskBoqBreakdown taskId={Task.data.id} /> : null}

      <div className="text-sm space-y-2 mb-6">
        <p>
          Idle Time - {Task?.data?.delay}
          {Task?.data?.delay === 1 ? 'Day' : 'Days'}
        </p>
        <p>
          Duration - {Task?.data?.duration} {Task?.data?.duration === 1 ? 'Day' : 'Days'}
        </p>

        {Task?.data?.dependencies?.length > 0 && (
          <p>
            Dependency Info -{' '}
            {Task?.data?.dependencies?.length
              ? Task.data.dependencies.map((dep) => dep.dependency?.name).join(', ')
              : '-'}
          </p>
        )}

        <p>Drawing Required - {Task?.data?.drawing_required ? 'Yes' : 'No'}</p>

        <div className="flex items-center gap-2">
          <p>Labour Budget -</p>

          {editingBudget ? (
            <>
              <input
                type="number"
                placeholder="Enter budget"
                value={newBudget}
                onChange={(e) => setNewBudget(e.target.value)}
                className="border p-1 rounded text-sm w-30"
              />
              <button
                onClick={handleLabourBudgetSave}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                disabled={isSubmitting}>
                Save
              </button>
              <button
                onClick={() => setEditingBudget(false)}
                className="text-blue-500 underline text-xs">
                cancel
              </button>
            </>
          ) : (
            <>
              {Task?.data?.labour_budget ? (
                <div className="flex items-center gap-2">
                  <span>{Task?.data?.labour_budget}</span>
                  <button
                    onClick={() => {
                      setNewBudget(Task?.data?.labour_budget); // Set existing value into input
                      setEditingBudget(true); // Open input box
                    }}
                    className="text-gray-500 hover:text-gray-700">
                    ✏️
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingBudget(true)}
                  className="text-blue-500 underline text-xs"
                  disabled={isSubmitting}>
                  Add
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <p>Material Budget -</p>

          {editingMaterialBudget ? (
            <>
              <input
                type="number"
                placeholder="Enter budget"
                value={materialBudget}
                onChange={(e) => setMaterialBudget(e.target.value)}
                className="border p-1 rounded text-sm w-30"
              />
              <button
                onClick={handleMaterialBudgetSave}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                disabled={isSubmitting}>
                Save
              </button>
              <button
                onClick={() => setEditingMaterialBudget(false)}
                className="text-blue-500 underline text-xs">
                cancel
              </button>
            </>
          ) : (
            <>
              {Task?.data?.material_budget ? (
                <div className="flex items-center gap-2">
                  <span>{Task?.data?.material_budget}</span>
                  <button
                    onClick={() => {
                      setMaterialBudget(Task?.data?.material_budget); // Set existing value into input
                      setEditingMaterialBudget(true); // Open input box
                    }}
                    className="text-gray-500 hover:text-gray-700">
                    ✏️
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingMaterialBudget(true)}
                  className="text-blue-500 underline text-xs"
                  disabled={isSubmitting}>
                  Add
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="text-sm mb-4">
        <div className="flex justify-between items-center mb-2">
          <p className="font-semibold">Material Details</p>
          <Button variant="primary" size="sm" onClick={openAddMaterials} disabled={isSubmitting}>
            Add Materials +
          </Button>
        </div>

        {Task?.data?.materialTaskMapping && Task?.data?.materialTaskMapping.length > 0 && (
          <div className="w-full mb-4 border border-gray-300 rounded-md overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2 border-b border-gray-300">Category</th>
                  <th className="text-left p-2 border-b border-gray-300">Item</th>
                  <th className="text-left p-2 border-b border-gray-300">Unit</th>
                  <th className="text-left p-2 border-b border-gray-300">Qty</th>
                  <th className="text-left p-2 border-b border-gray-300">Edit</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {handlePrepareTableData().map((row, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="text-left p-2 border-r border-gray-200">{row.category}</td>
                    <td className="text-left p-2 border-r border-gray-200">{row.item}</td>
                    <td className="text-left p-2 border-r border-gray-200">{row.unit}</td>
                    <td className="text-left p-2 border-r border-gray-200">{row.quantity}</td>
                    <td className="text-left p-2">
                      <button
                        onClick={() => {
                          handleEditMaterial(index, Task?.data?.materialTaskMapping[index]);
                          setShowMaterialModal(true);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        disabled={editingMaterialIndex !== null}
                        title="Edit material">
                        <img src={editIcon} alt="edit" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!Task?.data?.materialTaskMapping?.length && (
          <p className="text-gray-500 text-xs">No materials added yet.</p>
        )}
      </div>

      {showMaterialModal && (
        <Modal
          width={45}
          name={editingMaterialIndex !== null ? 'Edit Material' : 'Add Materials'}
          onClose={closeMaterialModal}>
          {editingMaterialIndex === null && (
            <div className="flex justify-end items-center mb-2 w-[100%]">
              <Button variant="ghost" size="sm" onClick={handleAddCategory}>
                Add Category +
              </Button>
            </div>
          )}

          {materialCategories.map((category, catIndex) => (
            <div key={category.id} className="border rounded-md p-4 mb-4 bg-gray-50 relative">
              <div className="flex justify-end items-center mb-2">
                {materialCategories.length > 1 && (
                  <Button
                    variant="danger-outline"
                    size="sm"
                    onClick={() => handleRemoveCategory(category.id)}>
                    Remove Category
                  </Button>
                )}
              </div>

              {/* 🛠 Move Category selector outside the items loop */}
              <div className="flex flex-col mb-4 gap-1">
                <label className="text-sm font-medium">Category {catIndex + 1}</label>
                <select
                  className="border p-2 rounded text-sm"
                  value={category.category_id || ''}
                  onChange={(e) => {
                    const selectedCategoryId = e.target.value;
                    handleCategoryChange(category.id, selectedCategoryId);
                  }}>
                  <option value="">Select Category</option>
                  {AllCategory?.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Items: Show Item + Quantity only */}
              <div className="space-y-6">
                {category.items.map((item) => {
                  return (
                    <div key={item.id} className="grid grid-cols-2 gap-4 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Item</label>
                        <select
                          className="border p-2 rounded text-sm"
                          value={item.item_id}
                          onChange={(e) => {
                            const selectedItemId = e.target.value;
                            const selectedItemData = category.available_items?.find(
                              (apiItem) => apiItem.id == selectedItemId
                            );
                            if (selectedItemData) {
                              handleItemChange(category.id, item.id, selectedItemData);
                            }
                          }}
                          disabled={!category.category_id}>
                          <option value="">Select Item</option>
                          {category.available_items?.map((apiItem) => (
                            <option key={apiItem.id} value={apiItem.id}>
                              {apiItem.name} ({apiItem.unit || 'No unit'})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex flex-col relative gap-1">
                        <label className="text-sm font-medium">Quantity</label>
                        <input
                          type="number"
                          placeholder="Enter quantity"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(category.id, item.id, e.target.value)}
                          className="border p-2 rounded text-sm w-full"
                        />
                        {category.items.length > 1 && (
                          <button
                            onClick={() => handleRemoveItem(category.id, item.id)}
                            className="absolute -top-2 -right-2 text-danger hover:opacity-80"
                            title="Remove item">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4">
                <Button variant="ghost" size="sm" onClick={() => handleAddItem(category.id)}>
                  Add item +
                </Button>
              </div>
            </div>
          ))}

          <div className="flex justify-end space-x-4 mt-6">
            <Button variant="secondary" onClick={closeMaterialModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isSubmitting}
              onClick={editingMaterialIndex !== null ? handleUpdateMaterial : handleAddUpdateMaterials}>
              {editingMaterialIndex !== null ? 'Update Material' : 'Add Materials'}
            </Button>
          </div>
        </Modal>
      )}
      {assigning && Task?.data?.id ? (
        <AssignTaskPeopleModal
          task={Task.data}
          initialSection={assigning}
          onClose={() => setAssigning(null)}
          onSaved={async () => {
            await refetchCommonLiveTask();
            await callApi?.();
          }}
        />
      ) : null}
    </div>
  );
};

export default SingleTaskDetails;
